import type { TableCopyMethod } from '@/analytics/enums/table.enums'
import type { TableEditResult } from '../schemas'
import { memo } from 'react'
import EditedIndicatorPopover from '@/components/edited-indicator/EditedIndicatorPopover'
import type { TableCell, TableColumn, TableEdit } from '../schemas'
import type { TableCellDraft, TableCellDrafts } from '../hooks/useTableCellDrafts'
import EditableCell from './EditableCell'
import TableCellOverflow from './TableCellOverflow'
import { TableMatchContext } from './table-match-context'

/** Keep unchanged cell editors and content out of virtual-scroll renders. */
const TableDataCell = memo(function TableDataCell({
  rowId,
  rowNumber,
  column,
  cell,
  draft,
  changeDraft,
  onEdit,
  matterId,
  tableId,
  canEdit,
  onCopy,
  copyRow,
  copyColumn,
  hideActions = false,
  selected = false,
  saving,
  pending,
  documentBased,
  rowEditing,
  height,
  search,
  activeOffset,
}: {
  rowId: string
  rowNumber: number
  column: TableColumn
  cell: TableCell | undefined
  draft: TableCellDraft | null
  changeDraft: TableCellDrafts['change']
  onEdit: (edit: TableEdit) => Promise<TableEditResult>
  matterId: string
  tableId?: string
  onCopy?: (row: number, column: number, footnotes: boolean, method?: TableCopyMethod) => Promise<void>
  copyRow: number
  copyColumn: number
  hideActions?: boolean
  selected?: boolean
  canEdit: boolean
  saving: boolean
  pending: boolean
  documentBased: boolean
  rowEditing: boolean
  height?: number
  search: string
  activeOffset?: number
}) {
  return (
    <>
      {draft && <span aria-hidden className="pointer-events-none absolute inset-0 z-[2] rounded border border-brand-400" />}
      {documentBased && cell?.edited_at && !draft && (
        <div data-table-copy-ignore>
          <EditedIndicatorPopover timestampMs={new Date(cell.edited_at).getTime()} />
        </div>
      )}
      <TableMatchContext.Provider value={search ? { offsets: cell?.search_offsets ?? [], length: Array.from(search).length, activeOffset } : null}>
        <TableCellOverflow editing={rowEditing || activeOffset !== undefined} height={height}>
          <EditableCell
            draft={draft}
            onDraftChange={(next) => changeDraft(rowId, column.id, next)}
            pending={pending}
            questionRevision={column.question_revision}
            cell={cell}
            label={`${column.name}, row ${rowNumber}`}
            matterId={matterId}
            tableId={tableId}
            canEdit={canEdit}
            allowFootnotes={documentBased}
            onCopy={onCopy ? (footnotes, method) => onCopy(copyRow, copyColumn, footnotes, method) : undefined}
            hideActions={hideActions}
            selected={selected}
            saving={saving}
            onSave={(value, expectedValue, content, expectedRevision, clearVerification, expectedQuestionRevision) =>
              onEdit({
                action: 'edit-cell',
                row_id: rowId,
                column_id: column.id,
                value,
                expected_value: expectedValue,
                expected_revision: expectedRevision,
                expected_question_revision: expectedQuestionRevision,
                clear_verification: clearVerification,
                content,
              })
            }
          />
        </TableCellOverflow>
      </TableMatchContext.Provider>
    </>
  )
})

export default TableDataCell
